/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColDef, GetRowIdParams, GridReadyEvent, RowSelectedEvent, ValueSetterParams } from '@ag-grid-community/core';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AgFns,
  GridOptionsExt,
  ISortModel,
  ProxAgFns,
  RowSelectionMultipleCheckbox,
  RowSelectionSingle,
  SaveAndQueryComponent,
  StatusService,
  wrapBusy,
} from '@core';

import { BaseService } from '@core';

import { UtilFns } from '@utils';

import * as _ from 'lodash';
import { EntityFns } from '@data';

import { Entity, EntityError } from 'breeze-client';
import {
  AccountAdminGroup,
  AccountAdmin,
  AccountUser,
  ApprovalTree,
  ApprovalTreeAdminGroup,
  ApprovalTreeUserGroup,
  Program,
  ProgramUserGroup,
  Account,
  ActiveStatusEnum,
  ActiveStatus,
  AccountPurchaseOrder,
  AccountProcurementCard,
  AccountAddress,
  ProgramTypeEnum,
} from '@models';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountAdminGroupFinderDialog } from '../account-administrator/account-admin-group-finder.dialog';
import { ProgramUserGroupPickerDialogComponent, PugPickerParams } from './program-user-group-picker.dialog';
import { Guid } from 'guid-typescript';
import { AccountActivationService } from '../services/account-activation.service';
import { DialogService } from 'primeng/dynamicdialog';
import { ApprovalTreeUserGroupDialogComponent } from './approval-tree-user-group.dialog.component';
import { ApprovalTreeDialogComponent } from './approval-tree.dialog.component';

@Component({
  selector: 'prox-approval-trees',
  templateUrl: './approval-trees.component.html',
  standalone: false,
})
export class ApprovalTreesComponent extends SaveAndQueryComponent {
  supplierId!: string;
  accountId!: string;
  account!: Account;
  accountAdminGroups!: AccountAdminGroup[];
  programUserGroups!: ProgramUserGroup[];
  atGridOptions!: GridOptionsExt;
  approvalTrees?: ApprovalTree[];
  selectedApprovalTree?: ApprovalTree;
  /** Owning Program per designated tree, for the Dedicated To column. */
  designatedTreeOwners: { ApprovalTreeId: string; ProgramId: string; ProgramName: string }[] = [];
  treeGridOptions!: GridOptionsExt;
  treeData!: any[];
  selectedTreeItem: any;
  userGridOptions!: GridOptionsExt;
  showUsers = false;
  showInactivePugs = false;
  dims = [40, 60, 99, 1];
  usersOrAdmins?: AccountUser[] | AccountAdmin[];
  isValidateActivated = false;
  activeStatuses: ActiveStatus[] = [];
  pos: AccountPurchaseOrder[] = [];
  pcards: AccountProcurementCard[] = [];
  billingAddresses: AccountAddress[] = [];

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    private statusService: StatusService,
    override dbSaveService: AccountDbSaveService,
    override dbQueryService: AccountDbQueryService,
    public activationService: AccountActivationService,
    public pngDialogService: DialogService,
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
  }

  override async updateFromParams(params: object): Promise<void> {
    this.accountId = params['accountId'];
    UtilFns.assertNonEmptyString(this.accountId, 'accountId');

    this.account = await this.dbQueryService.getAccountById(this.accountId);
    UtilFns.assertNonNull(this.account, 'Account');

    this.baseService.storeLocationParams(this.route);
    this.approvalTrees = await this.dbQueryService.getApprovalTrees(this.accountId);

    const [activeStatuses, pos, pcards, billingAddresses, treeOwners] = await Promise.all([
      this.dbQueryService.getAll(ActiveStatus),
      this.dbQueryService.getAccountPurchaseOrders(this.accountId),
      this.dbQueryService.getAccountProcurementCards(this.accountId),
      this.dbQueryService.getBillingAccountAddresses(this.accountId),
      this.dbQueryService.getDesignatedTreeOwners(this.accountId),
    ]);

    this.designatedTreeOwners = treeOwners;
    this.activeStatuses = activeStatuses;
    this.pos = pos;
    this.pcards = pcards;
    this.billingAddresses = billingAddresses;

    this.atGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onAtGridReady,
      onRowSelected: this.onAtRowSelected,
      rowModelType: 'clientSide',
      getRowId: (rowIdParams: GetRowIdParams) => {
        const at = rowIdParams.data as ApprovalTree;
        return at.id;
      },
    });

    this.isValidateActivated = this.activationService.hasErrors;
    if (this.isValidateActivated) {
      this.activationService.activate(this.account.id);
    }

    AgFns.captureGridRouteParams(this.atGridOptions, this.route, 'id');
    this.treeGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onTreeGridReady,
      onRowSelected: this.onTreeGridRowSelected,
      groupDefaultExpanded: -1,
      treeData: true,
      autoGroupColumnDef: {
        headerName: 'Group',
        width: 210,
        minWidth: 210,
        headerClass: 'ag-header-cell-auto-height',
      },
      getRowId: (rowIdParams: GetRowIdParams) => {
        const x = rowIdParams.data;
        return x.orgPath.join('|');
      },
      getDataPath: (data: any) => {
        return data.orgPath;
      },
    });

    this.userGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onUserGridReady,
      rowModelType: 'clientSide',
      getRowId: (rowIdParams: GetRowIdParams) => {
        const x = rowIdParams.data as AccountUser | AccountAdmin;
        return x?.id;
      },
    });

    this.setTitle('Proximity Organization in ' + this.account.name);
    this.isPageReady = true;
  }

  isReadOnly() {
    return this.statusService.getWorkingStatus(this.account).isActiveReadOnly;
  }

  statusMessage() {
    let msg = '';
    if (this.account?.activeStatusId == ActiveStatusEnum.Active) {
      return 'Account is Active and Not Editable.  To edit, set Account status to Hold.';
    } else if (this.account?.activeStatusId == ActiveStatusEnum.Hold) {
      msg = 'Account is On-Hold and is Editable';
    } else {
      return 'Account is ' + this.account?.activeStatus.name + ' and is Not Editable';
    }
    return msg;
  }

  onShowUsers() {
    if (!this.showUsers) {
      this.showUsers = true;
      this.dims = [25, 75, 50, 50];
    } else {
      this.showUsers = false;
      this.dims = [40, 60, 99, 1];
    }
  }

  addAdminGroupsLabel() {
    if (this.selectedApprovalTree) {
      return 'Add Administrator Groups to ' + this.selectedApprovalTree.name;
    }
    return 'Add Administrator Groups';
  }

  showUsersLabel() {
    if (!this.selectedTreeItem) {
      return '';
    }

    let sLabel = 'Show';
    if (this.showUsers) {
      sLabel = 'Hide';
    }

    if ((this.selectedTreeItem?.entity as Entity) instanceof ApprovalTreeAdminGroup) {
      sLabel +=
        ' Administrators in ' + (this.selectedTreeItem?.entity as ApprovalTreeAdminGroup)?.accountAdminGroup?.name + ' Administrator Group';
    }

    if ((this.selectedTreeItem?.entity as Entity) instanceof ApprovalTreeUserGroup) {
      sLabel += ' Users in ' + (this.selectedTreeItem?.entity as ApprovalTreeUserGroup)?.programUserGroup?.name + ' User Group';
    }

    return sLabel;
  }

  // Proximity Account Organization Tree grid
  onAtGridReady(event: GridReadyEvent<ApprovalTree>) {
    const [colDefs, sortModel] = this.getAtColDefsAndSortModel();

    AgFns.initGrid(event, colDefs, sortModel);
    AgFns.refreshGrid(this.atGridOptions, this.approvalTrees);
    AgFns.applyGridRouteParams(this.atGridOptions);
  }

  getAtColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      {
        ...AgFns.createButtonProps(' ', this.onAtEdit.bind(this), { label: 'Edit' }),
        ...AgFns.fixWidth(60),
        suppressHeaderMenuButton: true,
      },
      { headerName: 'Name', field: 'name', filter: 'agTextColumnFilter', ...AgFns.flexWidth(200) },
      {
        // A program-specific tree carries settings — purchase orders, procurement cards, ordering
        // time frames — that belong to ONE Program. Naming that Program here is the difference
        // between a list of interchangeable trees and a list that says which are spoken for.
        headerName: 'Dedicated To',
        colId: 'dedicatedTo',
        valueGetter: (p) => this.dedicatedToLabel(p.data as ApprovalTree),
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(200),
      },
      {
        headerName: 'Description',
        field: 'description',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(200),
      },
      ProxAgFns.getEntityDeleteColDef(this.onAtDelete.bind(this), {
        canDisplay: this.canAtDelete.bind(this),
        columnHide: this.isReadOnly(),
      }),
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];
    return [colDefs, sortModel] as const;
  }

  /**
   * What to show in the Dedicated To column. Blank for a shared tree — the common case, and
   * blank reads better than repeating "Shared" down every row.
   */
  private dedicatedToLabel(at?: ApprovalTree) {
    if (!at?.isProgramSpecific) return '';
    const owner = this.designatedTreeOwners.find((o) => o.ApprovalTreeId === at.id);
    return owner ? owner.ProgramName : 'Program-specific — not yet used by a Program';
  }

  onAtRowSelected(event: RowSelectedEvent<ApprovalTree>) {
    if (!event.node.isSelected()) return;
    const at = event.data as ApprovalTree;
    if (at == null) return;
    this.selectedApprovalTree = at;
    if (at.entityAspect.entityState.isDeleted()) {
      this.selectedApprovalTree = undefined;
      this.atGridOptions.apiX?.deselectAll();
      return;
    }
    this.updateGridRouteParams(this.atGridOptions, at.id);
    this.hydrateApprovalTree(at);
  }

  @wrapBusy
  async onAtAdd() {
    const newTree = this.dbSaveService.createEntity(ApprovalTree, { accountId: this.accountId });

    const result = await ApprovalTreeDialogComponent.openDialog(this.pngDialogService, {
      parent: this,
      approvalTree: newTree,
      isNew: true,
    });

    if (result) {
      this.approvalTrees?.push(newTree);
      await AgFns.refreshGrid(this.atGridOptions, this.approvalTrees);
      await AgFns.selectGridRowByKey(this.atGridOptions, (e) => e.id, newTree.id, 'name');
    }
    // Note: Dialog's onCancel already calls rejectChanges, no need to do it here
  }

  @wrapBusy
  async onAtEdit(approvalTree: ApprovalTree) {
    const result = await ApprovalTreeDialogComponent.openDialog(this.pngDialogService, {
      parent: this,
      approvalTree: approvalTree,
      isNew: false,
    });

    if (result) {
      this.atGridOptions.apiX?.redrawRows();
    }
  }

  canAtDelete(at: ApprovalTree) {
    if (this.isReadOnly()) return false;
    return true;
  }

  @wrapBusy
  async onAtDelete(at: ApprovalTree) {
    const programs = await this.dbQueryService.findInUse(at, Program, 'approvalTreeId');
    if (programs.length > 0) {
      this.toastr.warning(`You cannot delete a Proximity Organization Tree that is in use by any program.`, 'Cannot Delete');
      return false;
    }

    const ok = await this.dialogService.askIfOk(
      'Delete Proximity Organization Tree',
      `Are you sure you want to delete the Proximity Organization Tree:  ${at.name}?`,
    );
    if (!ok) return false;

    at.approvalTreeAdminGroups.slice().forEach((x) => this.onTreeAdminGroupDelete(x));
    EntityFns.deleteOrDetach(at.entityAspect);

    // Save immediately to database
    await this.dbSaveService.saveSelectedChanges([at]);
    this.toastr.success('Deleted', 'Database Activity');

    this.selectedApprovalTree = undefined;
    if (this.approvalTrees) {
      _.remove(this.approvalTrees, at);
      AgFns.refreshGrid(this.atGridOptions, this.approvalTrees);
    }
    // forces clearing of the tree grid
    this.hydrateApprovalTree(undefined);
    await AgFns.selectFirstRow(this.atGridOptions);
    return true;
  }
  // ---------- Tree  Grid ------------------------------

  viewLabel(): string {
    return this.isReadOnly() ? 'View' : 'Edit';
  }

  onTreeGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        ...AgFns.createIconProps('', '', () => null, { label: '', getIcon: this.getIcon.bind(this) }),
        ...AgFns.fixWidth(40),
      },
      { headerName: 'Type', field: 'type', sortable: false, ...AgFns.fixWidth(180) },
      {
        headerName: 'Status',
        sortable: false,
        // Effective status: if the underlying PUG is Inactive, that wins over the ATUG link's
        // own status — the row isn't really live regardless of what the ATUG says. This surfaces
        // consolidated (or otherwise deactivated) PUGs whose ATUG link is still Active.
        valueGetter: (p) => {
          const atug = p.data?.entity;
          if (!(atug instanceof ApprovalTreeUserGroup)) return '';
          if (atug.programUserGroup?.activeStatusId === ActiveStatusEnum.Inactive) {
            return atug.programUserGroup.activeStatus?.name;
          }
          return atug.activeStatus?.name;
        },
        // Match the effective-status logic for coloring. Uses the qb-status-* utility classes
        // from forms-quickbooks.scss (same classes ProxAgFns.getActiveStatusColDef applies).
        cellClass: (p) => {
          const atug = p.data?.entity;
          if (!(atug instanceof ApprovalTreeUserGroup)) return '';
          const effectiveStatusId =
            atug.programUserGroup?.activeStatusId === ActiveStatusEnum.Inactive ? ActiveStatusEnum.Inactive : atug.activeStatusId;
          if (effectiveStatusId === ActiveStatusEnum.Inactive) return 'qb-status-inactive';
          if (effectiveStatusId === ActiveStatusEnum.Hold) return 'qb-status-hold';
          return '';
        },
        ...AgFns.fixWidth(100),
      },
      {
        ...AgFns.createButtonProps('View/Edit', this.onGotoGroup.bind(this), { label: this.viewLabel() }),
        headerClass: 'ag-header-center',
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Approval Limit',
        editable: (item) => {
          return (
            !this.isReadOnly() &&
            item.data.entity instanceof ApprovalTreeAdminGroup &&
            item.data.entity.parentApprovalTreeAdminGroupId != null
          );
        },
        sortable: false,
        type: 'rightAligned',
        cellStyle: { 'justify-content': 'flex-end' },
        valueGetter: (p) => {
          const atag = p.data.entity as ApprovalTreeAdminGroup;
          if (atag instanceof ApprovalTreeUserGroup) return '';
          if (atag.parentApprovalTreeAdminGroupId == null) return ' - No Limit -';
          const amt = atag.budgetApprovalLimitAmt;
          return amt ? UtilFns.fmtCurrencyA(amt, 2) : '';
        },
        valueSetter: (p: ValueSetterParams) => {
          const atag = p.data.entity as ApprovalTreeAdminGroup;
          if (!atag) return false;
          if (atag.parentApprovalTreeAdminGroupId == null) return false;
          atag.budgetApprovalLimitAmt = UtilFns.parseNumber(p.newValue);
          return true;
        },
        ...AgFns.fixWidth(220),
      },
      {
        headerName: 'Approvals Amt YTD',

        sortable: false,
        type: 'rightAligned',
        cellStyle: { 'justify-content': 'flex-end' },
        valueGetter: (p) => {
          const atag = p.data.entity as ApprovalTreeAdminGroup;
          if (atag instanceof ApprovalTreeUserGroup) return '';
          const amt = atag.calcPrevAmtFromLogs();
          return amt ? UtilFns.fmtCurrencyA(amt, 2) : '';
        },
        ...AgFns.fixWidth(220),
      },
      {
        ...AgFns.createButtonProps('', this.onTreeAttachToAdminGroup.bind(this), {
          label: 'Attach Admin Group',
          style: 'button',
          canDisplay: this.canAttachGroup.bind(this),
          columnHide: this.isReadOnly(),
          colId: 'attachAdminGroupColId',
        }),
        ...AgFns.fixWidth(150),
      },
      {
        ...AgFns.createButtonProps('', this.onTreeAttachToUserGroup.bind(this), {
          label: 'Attach User Group',
          style: 'button',
          canDisplay: this.canAttachGroup.bind(this),
          columnHide: this.isReadOnly(),
          colId: 'attachUserGroupColId',
        }),
        ...AgFns.fixWidth(150),
      },
      {
        ...AgFns.createButtonProps('', this.onTreeMoveGroup.bind(this), {
          label: 'Move Group',
          style: 'button',
          canDisplay: this.canMoveGroup.bind(this),
          columnHide: this.isReadOnly(),
        }),
        ...AgFns.fixWidth(130),
      },

      // Status change — only meaningful for ATUG (PUG-in-tree) rows. Restricted to Active/Hold
      // (see project_order-readiness-diagnostic memory: PUG-on-Hold silently filters programs out
      // of users' place-order cards, and admins need a place to toggle the per-tree linkage).
      ProxAgFns.getEntityStatusColDef(this.onChangeAtugActiveStatus.bind(this), {
        canDisplay: this.canChangeAtugStatus.bind(this),
        columnHide: this.isReadOnly(),
      }),

      // { headerName: 'Budget Trigger Amt', field: 'budgetTriggerAmt', filter: 'agNumberColumnFilter', editable: true, sortable: false },
      ProxAgFns.getEntityDeleteColDef(this.onTreeItemDelete.bind(this), { canDisplay: this.canTreeItemDelete.bind(this) }),
    ];
    // don't sort grids with a DisplayOrderColumnDef
    const sortModel: ISortModel = [];

    AgFns.initGrid(event, colDefs, sortModel);
    AgFns.selectFirstRow(this.treeGridOptions, 'name');
  }

  async onGotoGroup(treeItem: any) {
    const entity = treeItem.entity as Entity;
    if (entity instanceof ApprovalTreeAdminGroup) {
      this.router.navigate(['account/accounts', this.accountId, 'manage', 'account-admin-groups'], {
        queryParams: { key: entity.accountAdminGroupId },
      });
    } else if (entity instanceof ApprovalTreeUserGroup) {
      const activePrograms = await this.dbQueryService.getActiveProgramsForApprovalTree((<ApprovalTreeUserGroup>entity).approvalTreeId);
      const ap = activePrograms.map((x) => {
        return x.name;
      });
      if (activePrograms.length > 0) {
        await this.dialogService.okDialog(
          'Cannot Edit',
          'The following program(s) must be HELD before this Program User Group can be edited:<br><br>' + ap.join('<br>'),
        );
      }
      // Check all programs (not just active) to determine if this is an Inventory program
      const allPrograms = await this.dbQueryService.getProgramsForApprovalTree((<ApprovalTreeUserGroup>entity).approvalTreeId);
      const isInventoryProgram = allPrograms.some((p) => p.programTypeId === ProgramTypeEnum.Inventory);
      const ok = await ApprovalTreeUserGroupDialogComponent.openDialog(this.pngDialogService, {
        parent: this,
        atug: entity,
        isEditable: activePrograms.length == 0,
        isInventoryProgram,
      });

      // if (ok) {

      // }
      // this.router.navigate(['account/accounts', this.accountId, 'manage', 'program-user-groups'], {
      //   queryParams: { key: entity.programUserGroupId },
      // });
    }
  }

  async onTreeGridRowSelected(event: RowSelectedEvent) {
    if (!event.node.isSelected()) return;
    const group = event.data;
    if (group == null) return;
    this.selectedTreeItem = group;
    const entity = group.entity as Entity;
    if (entity.entityAspect.entityState.isDeleted()) {
      this.selectedApprovalTree = undefined;
      this.atGridOptions.apiX?.deselectAll();
      return;
    }
    if (entity instanceof ApprovalTreeAdminGroup) {
      this.usersOrAdmins = await this.dbQueryService.getAccountAdminGroupAdmins(entity.accountAdminGroupId);
    } else if (entity instanceof ApprovalTreeUserGroup) {
      this.usersOrAdmins = await this.dbQueryService.getAccountUsersForProgramUserGroup(entity.programUserGroupId);
    }
    this.userGridOptions.apiX?.setColumnsVisible(['adminType'], entity instanceof ApprovalTreeAdminGroup);
    AgFns.refreshGrid(this.userGridOptions, this.usersOrAdmins);
  }

  canAttachGroup(treeItem: any) {
    const e = treeItem.entity as ApprovalTreeAdminGroup | ApprovalTreeUserGroup;
    return e instanceof ApprovalTreeAdminGroup && !this.isReadOnly();
  }

  @wrapBusy
  async onTreeAttachToAdminGroup(treeItem?: any) {
    if (this.selectedApprovalTree == null) return;
    const approvalTree = this.selectedApprovalTree;
    let parentId: string | undefined = undefined;
    if (treeItem != null) {
      const atAg = treeItem.entity as ApprovalTreeAdminGroup;
      parentId = atAg.id;
    }

    if (!this.accountAdminGroups) {
      this.accountAdminGroups = await this.dbQueryService.getAccountAdminGroups(this.accountId);
    }
    const aagSet = new Set(approvalTree.approvalTreeAdminGroups.map((x) => x.accountAdminGroup));
    const unusedAccountAdminGroups = this.accountAdminGroups.filter((x) => !aagSet.has(x));
    const newAdminGroups = await this.dialogService.createFinder(AccountAdminGroupFinderDialog, {
      accountAdminGroups: unusedAccountAdminGroups,
      rowSelection: RowSelectionMultipleCheckbox,
    });

    const atAgs = newAdminGroups.map((ent) => {
      return this.dbSaveService.createEntity(ApprovalTreeAdminGroup, {
        id: Guid.create().toString(),
        approvalTreeId: approvalTree.id,
        accountAdminGroupId: ent.id,
        parentApprovalTreeAdminGroupId: parentId,
      });
    });

    this.hydrateApprovalTree(approvalTree);
  }

  @wrapBusy
  async onTreeAttachToUserGroup(treeItem: any) {
    const atAg = treeItem.entity as ApprovalTreeAdminGroup;
    const approvalTree = atAg.approvalTree;

    // Refuse to attach a new PUG while any program using this Proximity Organization Tree is Active.
    // Program-level validators (e.g. "PUG must have an allowance OR issuance limit")
    // only run at activation time, so attaching to an Active program bypasses them
    // and leaves the PUG in a silently-broken state. Mirrors the guard in onGotoGroup.
    const activePrograms = await this.dbQueryService.getActiveProgramsForApprovalTree(approvalTree.id);
    if (activePrograms.length > 0) {
      await this.dialogService.okDialog(
        'Cannot Attach',
        'The following program(s) must be HELD before a new Program User Group can be attached:<br><br>' +
          activePrograms.map((x) => x.name).join('<br>'),
      );
      return;
    }

    // Collect PUGs already attached anywhere in this tree — they're hidden from the picker.
    const attachedPugIds = _.flatten(
      approvalTree.approvalTreeAdminGroups.map((x) => x.approvalTreeUserGroups.map((y) => y.programUserGroupId)),
    );

    // Purpose-built PUG picker (see program-user-group-picker.dialog.ts for the "why".)
    // Returns {Id, Name}[] via a plain HTTP call, no Breeze.
    const params: PugPickerParams = {
      accountId: this.accountId,
      excludePugIds: attachedPugIds,
      // Both opt-in on this caller: an approval tree has no server-side Inactive gate, and
      // bulk-attaching a whole account's PUGs to a tree admin group is a real action.
      allowShowInactive: true,
      allowSelectAll: true,
    };
    const picked = (await this.dialogService.create(ProgramUserGroupPickerDialogComponent, params, {
      header: 'Select Program User Groups',
      width: 'calc(50vw)',
    })) as { Id: string; Name: string }[];

    if (!picked || picked.length === 0) return;

    // The picker bypasses Breeze, so the picked PUGs aren't in the local EntityManager yet.
    // hydrateApprovalTree() dereferences atug.programUserGroup.name (see line 787/792), so we
    // need each picked PUG loaded into Breeze cache before creating the ATUGs. Small n (only
    // the picked ids) — no risk of re-triggering the self-ref materialization bug that made
    // us bypass Breeze in the picker in the first place.
    const pickedIds = picked.map((p) => p.Id);
    await this.dbQueryService.uow
      .createQuery(ProgramUserGroup)
      .where({ id: { in: pickedIds } })
      .execute();

    const atPugs = picked.map((p) => {
      return this.dbSaveService.createEntity(ApprovalTreeUserGroup, {
        approvalTreeId: approvalTree.id,
        approvalTreeAdminGroupId: atAg.id,
        programUserGroupId: p.Id,
        activeStatusId: ActiveStatusEnum.Active,
      });
    });

    this.hydrateApprovalTree(approvalTree);
  }

  canMoveGroup(treeItem: any) {
    const e = treeItem.entity as ApprovalTreeAdminGroup | ApprovalTreeUserGroup;
    if (this.isReadOnly()) return false;
    if (e instanceof ApprovalTreeAdminGroup && e.parentApprovalTreeAdminGroupId == null) {
      return false;
    }
    return true;
  }

  /** ATUG status icon shows only on PUG rows (ATUGs); admin-group rows have no per-tree status. */
  canChangeAtugStatus(treeItem: any) {
    if (this.isReadOnly()) return false;
    return treeItem?.entity instanceof ApprovalTreeUserGroup;
  }

  async onChangeAtugActiveStatus(treeItem: any) {
    const atug = treeItem?.entity as ApprovalTreeUserGroup;
    if (atug == null) return;
    await this.statusService.onChangeActiveStatus(
      this,
      atug,
      'Proximity Organization Tree User Group',
      undefined,
      // Only Active and Hold are meaningful for an ATUG; hide Phaseout and Inactive from the picker.
      (s) => s.id === ActiveStatusEnum.Active || s.id === ActiveStatusEnum.Hold,
    );
    // Tree-grid rows wrap the entity in { orgPath, entity, ... }; refreshCells diffs the wrapper
    // and skips re-evaluating valueGetters that read entity.*. redrawRows forces a full rebuild.
    this.treeGridOptions.apiX?.redrawRows();
  }

  @wrapBusy
  async onTreeMoveGroup(treeItem: any) {
    const e = treeItem.entity as ApprovalTreeAdminGroup | ApprovalTreeUserGroup;
    const at = e.approvalTree;
    let atags = e.approvalTree.approvalTreeAdminGroups;
    if (e instanceof ApprovalTreeAdminGroup) {
      atags = atags.filter((aag) => aag.id != e.id && aag.id != e.parentApprovalTreeAdminGroupId);
    } else {
      atags = atags.filter((aag) => aag.id != e.approvalTreeAdminGroupId);
    }
    const aags = atags.map((x) => x.accountAdminGroup);
    const moveToAags = await this.dialogService.createFinder(AccountAdminGroupFinderDialog, {
      title: 'Select destination Account Admin Group to move to',
      accountAdminGroups: aags,
      rowSelection: RowSelectionSingle,
    });

    if (moveToAags == null || moveToAags.length == 0) return false;
    const toAtag = atags.find((atag) => atag.accountAdminGroupId == moveToAags[0].id);
    UtilFns.assertNonNull(toAtag, 'toAtag - ApprovalTreeAdminGroup to move to not found');
    if (e instanceof ApprovalTreeAdminGroup) {
      e.parentApprovalTreeAdminGroupId = toAtag.id;
    } else {
      // can't modify the 'approvalTreeAdminGroupId' of the ApprovalTreeUserGroup so we need to delete and readd it
      // e.approvalTreeAdminGroupId = toAtag.id;
      const atugClone = EntityFns.cloneStruct(e, ['approvalTreeAdminGroupId']) as ApprovalTreeUserGroup;
      atugClone.approvalTreeAdminGroupId = toAtag.id;
      const atugNew = this.dbSaveService.createEntity(ApprovalTreeUserGroup, atugClone);
      EntityFns.deleteOrDetach(e.entityAspect);
    }

    this.hydrateApprovalTree(at);

    return true;
  }

  canTreeItemDelete(treeItem: any) {
    if (this.selectedApprovalTree == null || this.isReadOnly()) return false;
    const e = treeItem.entity as ApprovalTreeAdminGroup | ApprovalTreeUserGroup;
    return true;
  }

  @wrapBusy
  async onTreeItemDelete(treeItem: any) {
    if (this.selectedApprovalTree == null) return false;
    const e = treeItem.entity as ApprovalTreeAdminGroup | ApprovalTreeUserGroup;
    // Guard against double-click on a ghost row still visible in the grid: once the entity is
    // Deleted/Detached, Breeze severs .approvalTree, so findInUse would crash on null.id.
    if (EntityFns.isDeletedOrDetached(e.entityAspect)) return false;
    const programs = await this.dbQueryService.findInUse(e.approvalTree, Program, 'approvalTreeId');
    if (programs.some((p) => p.activeStatusId == ActiveStatusEnum.Active)) {
      await this.dialogService.okDialog(
        'Cannot Delete',
        `You cannot delete a Proximity Organization Tree Admin/User Group that is in use by a program that is Active
        <br/><br/>The following programs are active: <br/> ${programs.map((x) => x.name).join('<br/>')}`,
      );
      return false;
    }
    if (e instanceof ApprovalTreeAdminGroup) {
      await this.onTreeAdminGroupDelete(e);
    } else {
      await this.onTreeUserGroupDelete(e);
    }
    this.hydrateApprovalTree(this.selectedApprovalTree);
    return true;
  }

  async onTreeUserGroupDelete(atug: ApprovalTreeUserGroup) {
    // No-stranded-orders guard: refuse removal if this PUG has any unplaced orders (Setup /
    // AwaitingApproval / AwaitingPlacement / OnHold). Removing the ATUG would sever the PUG
    // from this Program's Proximity Organization Tree while the orders still point at the PUG — likely to
    // silently break approval routing and placement.
    const pug = atug.programUserGroup;
    if (pug) {
      const unplaced = await this.dbQueryService.getUnplacedOrderCountForPug(pug.id);
      if (unplaced > 0) {
        await this.dialogService.okDialog(
          'Cannot Remove',
          `The Program User Group "${pug.name}" has ${unplaced} unplaced order(s) that must be resolved first. ` +
            `Delete Setup orders, push Awaiting-Approval orders through approve/reject, or wait for the ` +
            `approval process to complete, then try again.`,
        );
        return;
      }
    }
    EntityFns.deleteOrDetach(atug.entityAspect);
  }

  // recursive deletion
  @wrapBusy
  async onTreeAdminGroupDelete(atag: ApprovalTreeAdminGroup) {
    atag.childrenApprovalTreeAdminGroups.slice().forEach((e) => this.onTreeAdminGroupDelete(e));
    atag.approvalTreeAdminGroupLogs.slice().forEach((e) => EntityFns.deleteOrDetach(e.entityAspect));
    atag.approvalTreeUserGroups.slice().forEach((e) => EntityFns.deleteOrDetach(e.entityAspect));

    EntityFns.deleteOrDetach(atag.entityAspect);
  }

  getIcon(treeItem: any) {
    const e = treeItem.entity as ApprovalTreeAdminGroup | ApprovalTreeUserGroup;
    if (e instanceof ApprovalTreeAdminGroup) {
      return 'fa-regular fa-sitemap';
    } else {
      return 'pi pi-user';
    }
  }

  // -------------- Users or Admins Grid --------------------------

  onUserGridReady(event: GridReadyEvent<ApprovalTree>) {
    const [colDefs, sortModel] = this.getUserColDefsAndSortModel();
    AgFns.initGrid(event, colDefs, sortModel);
  }

  getUserColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      { headerName: 'Last Name', field: 'proximityUser.lastName', ...AgFns.flexWidth(200), filter: 'agTextColumnFilter' },
      { headerName: 'First Name', field: 'proximityUser.firstName', ...AgFns.flexWidth(200), filter: 'agTextColumnFilter' },
      { headerName: 'Middle Name', field: 'proximityUser.middleName', ...AgFns.flexWidth(150), filter: 'agTextColumnFilter' },
      { headerName: 'Salutation', field: 'proximityUser.salutation', ...AgFns.flexWidth(100), filter: 'agTextColumnFilter' },
      { headerName: 'Email', field: 'proximityUser.email', ...AgFns.flexWidth(200), filter: 'agTextColumnFilter' },
      {
        // The role decides who may approve: an Order Administrator in a group is not an approver.
        // Shown for Admin Group rows only; onTreeGridRowSelected hides it for Program User Groups.
        headerName: 'Admin Type',
        colId: 'adminType',
        valueGetter: (p) => {
          const role = (p.data as AccountAdmin)?.proximityUser?.proximityRole;
          return role?.description ?? role?.name ?? '';
        },
        ...AgFns.flexWidth(180),
        filter: 'agTextColumnFilter',
      },
      { ...ProxAgFns.getActiveStatusColDef('proximityUser.activeStatus.name'), ...AgFns.fixWidth('status') },
    ];
    const sortModel: ISortModel = [
      { colId: 'proximityUser.lastName', sort: 'asc' },
      { colId: 'proximityUser.firstName', sort: 'asc' },
    ];
    return [colDefs, sortModel] as const;
  }

  // -------------- Prepare Proximity Account Organization Tree for display -----------------------

  async hydrateApprovalTree(at?: ApprovalTree) {
    const treeData: any[] = [];
    if (at != null) {
      // Filter deleted/detached entities out of the tree — Breeze navigation collections still
      // include them, which would leave "ghost rows" visible after a delete and let the user
      // double-click a row that no longer exists.
      at.approvalTreeAdminGroups
        .filter((atag) => atag.parentApprovalTreeAdminGroup == null && !EntityFns.isDeletedOrDetached(atag.entityAspect))
        .forEach((atag) => {
          treeData.push(...this.hydrateApprovalTreeAdminGroup(atag, []));
        });
      // this.treeData = treeData;
      this.treeData = _.sortBy(treeData, (x) => x.name);
    }
    AgFns.refreshGrid(this.treeGridOptions, this.treeData);
    this.usersOrAdmins = [];
    AgFns.refreshGrid(this.userGridOptions, this.usersOrAdmins);

    AgFns.selectFirstRow(this.treeGridOptions);
    return treeData;
  }

  hydrateApprovalTreeAdminGroup(atag: ApprovalTreeAdminGroup, parentPath: string[]) {
    const treeData: any[] = [];
    const path = [...parentPath, atag.accountAdminGroup.name];
    treeData.push({
      orgPath: path,
      entity: atag,
      type: 'Admin Group',
      name: atag.accountAdminGroup.name,
      adminGroup: atag.accountAdminGroup,
    });
    atag.approvalTreeUserGroups
      .filter((child) => !EntityFns.isDeletedOrDetached(child.entityAspect))
      .forEach((child) => {
        treeData.push(...this.hydrateApprovalTreeUserGroup(child, path));
      });
    atag.childrenApprovalTreeAdminGroups
      .filter((child) => !EntityFns.isDeletedOrDetached(child.entityAspect))
      .forEach((child) => {
        treeData.push(...this.hydrateApprovalTreeAdminGroup(child, path));
      });
    return treeData;
  }

  hydrateApprovalTreeUserGroup(atug: ApprovalTreeUserGroup, parentPath: string[]) {
    const treeData: any[] = [];
    // Show-Inactive semantics: hide only Inactive PUGs when unchecked; keep Hold + Phaseout.
    // See show-inactive-checkbox-semantics memory.
    if (!this.showInactivePugs && atug.programUserGroup?.activeStatusId === ActiveStatusEnum.Inactive) {
      return treeData;
    }
    const path = [...parentPath, atug.programUserGroup.name];
    treeData.push({
      orgPath: path,
      entity: atug,
      type: 'Program User Group',
      name: atug.programUserGroup.name,
      userGroup: atug.programUserGroup,
    });
    return treeData;
  }

  onShowInactivePugsChanged() {
    this.hydrateApprovalTree(this.selectedApprovalTree);
  }

  // ---------------------------------------------------------------------------------------

  override async addCrossValidationErrors() {
    if (!this.approvalTrees) return;
    EntityFns.checkForDupErrors(
      this.approvalTrees,
      (e) => e.name,
      (e, dupName) => this.createValidationError(e, 'name', `This name: '${dupName}' is a duplicate.`),
    );

    this.approvalTrees.forEach((x) =>
      x.approvalTreeAdminGroups.forEach((y) => {
        if (y.parentApprovalTreeAdminGroupId != null) {
          if (y.budgetApprovalLimitAmt == null || y.budgetApprovalLimitAmt == 0) {
            this.createValidationError(
              y,
              'budgetApprovalLimitAmt',
              `Admin Groups must have an approval limit > 0 :  ${y.accountAdminGroup.name}`,
            );
          } else if (
            y.parentApprovalTreeAdminGroup.parentApprovalTreeAdminGroupId != null &&
            y.budgetApprovalLimitAmt >= (y.parentApprovalTreeAdminGroup?.budgetApprovalLimitAmt ?? 0)
          ) {
            this.createValidationError(
              y,
              'budgetApprovalLimitAmt',
              `An Admin Groups must have a budget limit less than its parent admin group:  ${y.accountAdminGroup.name}`,
            );
          }
        }
      }),
    );
  }

  override async afterSave() {
    if (this.isValidateActivated) {
      if (!(await this.activationService.activate(this.account.id))) {
        this.account.activeStatusId = ActiveStatusEnum.Active;
        this.activationService.deactivate();
        this.isValidateActivated = false;
        await this.saveChanges();
        await this.dialogService.okDialog('Activation Status', "Account is successfully updated to 'Active'");
      }
    }
    return this.afterUndo();
  }

  override async afterUndo() {
    this.approvalTrees = await this.dbQueryService.getApprovalTrees(this.accountId);
    AgFns.refreshGrid(this.atGridOptions, this.approvalTrees);

    if (this.selectedApprovalTree == null || this.selectedApprovalTree.entityAspect.entityState.isDetached()) {
      AgFns.selectFirstRow(this.atGridOptions);
    } else {
      this.hydrateApprovalTree(this.selectedApprovalTree);
    }

    AgFns.selectGridRowByKey(this.atGridOptions, (e) => e.id, this.selectedApprovalTree?.id || '');
  }

  override navigateToValidationError(error: EntityError) {
    const errEnt = error.entity;
    const prop = error.propertyName;
    if (errEnt instanceof ApprovalTree) {
      AgFns.selectGridRowByKey(this.atGridOptions, (e: ApprovalTree) => e.id, errEnt.id);
      // } else  if (errEnt instanceof ApprovalTreeDetail) {
      //   AgFns.selectGridRowByKey(this.atGridOptions, (e: ApprovalTree) => e.id, errEnt.approvalChainId);
      //   AgFns.selectGridRowByKey(this.atDetailGridOptions, (e: ApprovalTreeDetail) => e.accountUserGroupId, errEnt.accountUserGroupId, prop);
    }
  }
}
